import axiosInstance from '../../../fetchConfig/axiosConfig';
import { completeCaseStatus } from '../../../common/CaseStatusUpdater';
import { 
  DEFAULT_BAD_DATA_FIELDS,
} from '../../../common/mappings.js';
import { buildColumnsFromRecords } from './columnDefinitions';
import { isCaseSettled } from '../SummaryCaseTable/utils/helpers';
import { checkTaskExistence } from '../../../utils/taskUtils';
import { usePersistedDataStore } from '../../store';
import { toast } from '../../../common/toast';
import logger from '../../../utils/logger';

// Fetch bad data report given parameter object.
// Returns { records, total_rows } from backend.
export async function fetchBadDataReport(params, { initialInvocation = false, setDataAnalysisResult, setDataAnalysisColumns, setDataAnalysisFilterModel, filterModel = null } = {}) {
  try {
    // Use default fields if none provided
    const requestParams = {
      ...params,
      BadDataFields: params.BadDataFields || DEFAULT_BAD_DATA_FIELDS
    };

    // logger.log('requestParams:', requestParams);
    // If overrideIds is present, set uids to overrideIds (overwrite any other uids)
    if (params.overrideIds && params.overrideIds.length > 0) {
      requestParams.uids = params.overrideIds;
    }

    // We need the Client field to come back from bad data result
    const response = await axiosInstance.post('/flag_bad_data', requestParams);
    
    let records = response.data.records || [];
    const negotiationObj = usePersistedDataStore.getState().negotiationObj;
    const returnedParcelIds = new Set(
      records.map((record) => record?.parcel_id).filter((id) => id != null && id !== '').map(String)
    );
    
    const isReturnAllSelected = requestParams.BadDataFields?.includes('Return All');
    const showSettledCases = negotiationObj?.showSettledCases;
    
    if (!isReturnAllSelected && !showSettledCases) {
      records = records.filter(record => {
        if (record.Client !== 1) return true;
        return !isCaseSettled(record);
      });
    }
    const total_rows = response.data.total_rows || 0;
    const client_count = records.filter(record => record.Client === 1).length;
    const badDataFields = requestParams.BadDataFields || [];
    const hasRequiredFields = ['Sqft', 'Acres', 'Style'].every((field) => badDataFields.includes(field));
    const runUids = Array.isArray(negotiationObj?.uids)
      ? negotiationObj.uids.filter((uid) => uid != null && uid !== '')
      : [];
    const cleanUids = runUids.filter((uid) => !returnedParcelIds.has(String(uid)));

    // Set the records in the store
    if (setDataAnalysisResult) {
      setDataAnalysisResult(records);
    }

    // Default to clients filter if none is provided (keeps reruns consistent with initial run)
    const defaultClientsFilterModel = {
      items: [{ id: 1, field: 'Client', operator: '=', value: '1' }],
      logicOperator: 'and'
    };

    // On initial invocation, set up columns
    if (initialInvocation && records.length > 0) {
      // Build and set columns
      if (setDataAnalysisColumns) {
        const columns = buildColumnsFromRecords(records);
        setDataAnalysisColumns(columns);
      }
    }

    // Always set a filter model if a setter is provided:
    // - prefer caller-provided filterModel (preserves current tab/filters on rerun)
    // - otherwise default to Client=1 (matches initial behavior)
    if (setDataAnalysisFilterModel) {
      const nextFilterModel = filterModel?.items?.length ? filterModel : defaultClientsFilterModel;
      setDataAnalysisFilterModel(nextFilterModel);
    }

    await checkTaskExistence();

    let dataCleaningAutoCompleteRan = false;
    if (hasRequiredFields && cleanUids.length > 0) {
      try {
        const updateResult = await completeCaseStatus(cleanUids, 'DataCleaning', {
          muniCode: negotiationObj?.MuniCode || params.MuniCode || 'All',
          taxYear: negotiationObj?.TaxYear || params.TaxYear,
          onlyIfOpen: true,
          completeAllCaseTypes: true,
        });
        dataCleaningAutoCompleteRan = true;
        if ((updateResult?.updated_count || 0) > 0) {
          toast.success('Data Cleaning Tasks have been marked completed');
        }
      } catch (updateErr) {
        logger.error('[DATA_ANALYSIS] auto-complete failed', updateErr);
      }
    }

    return {
      records,
      total_rows,
      client_count,
      dataCleaningAutoCompleteRan,
    };
  } catch (error) {
    logger.error('Error in fetchBadDataReport:', error);
    throw error;
  }
} 